import type { TFunction } from "i18next";
import {
  BillingCycle,
  InvoiceStatus,
  InvoiceType,
  PaymentMethod,
  PaymentStatus,
  MeterKind,
  MeterLimitType,
  PlanType,
  RechargeStatus,
  SubscriptionStatus,
} from "../types/licensing.types";

/** Indian-market default; the settings carry the currency code. */
export function formatMoney(amount: number | null | undefined, currency = "INR"): string {
  if (amount === null || amount === undefined) return "—";
  try {
    return new Intl.NumberFormat(currency === "INR" ? "en-IN" : undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

export function formatQuantity(q: number | null | undefined, unit?: string | null): string {
  if (q === null || q === undefined) return "∞";
  const n = Math.abs(q) >= 1000 ? new Intl.NumberFormat("en-IN").format(Math.round(q)) : q % 1 === 0 ? String(q) : q.toFixed(2);
  return unit ? `${n} ${unit}` : n;
}

export function subscriptionStatusLabel(status: number | null | undefined, t: TFunction): string {
  switch (status) {
    case SubscriptionStatus.Trial: return t("status.trial", { defaultValue: "Trial" });
    case SubscriptionStatus.Active: return t("status.active", { defaultValue: "Active" });
    case SubscriptionStatus.PastDue: return t("status.pastDue", { defaultValue: "Past due" });
    case SubscriptionStatus.Suspended: return t("status.suspended", { defaultValue: "Suspended" });
    case SubscriptionStatus.Cancelled: return t("status.cancelled", { defaultValue: "Cancelled" });
    case SubscriptionStatus.Expired: return t("status.expired", { defaultValue: "Expired" });
    default: return t("status.none", { defaultValue: "No subscription" });
  }
}

/** Sizing shared by every status badge so chips line up regardless of label length. */
export const STATUS_CHIP = "inline-flex items-center justify-center min-w-[6.5rem] whitespace-nowrap";

export function subscriptionStatusClass(status: number | null | undefined): string {
  switch (status) {
    case SubscriptionStatus.Active: return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 " + STATUS_CHIP;
    case SubscriptionStatus.Trial: return "bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/30 " + STATUS_CHIP;
    case SubscriptionStatus.PastDue: return "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 " + STATUS_CHIP;
    case SubscriptionStatus.Suspended:
    case SubscriptionStatus.Cancelled:
    case SubscriptionStatus.Expired: return "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30 " + STATUS_CHIP;
    default: return "bg-[rgb(var(--card-muted))] text-[rgb(var(--text-muted))] border-[rgb(var(--border))] " + STATUS_CHIP;
  }
}

export function billingCycleLabel(cycle: number | null | undefined, t: TFunction): string {
  switch (cycle) {
    case BillingCycle.Monthly: return t("cycle.monthly", { defaultValue: "Monthly" });
    case BillingCycle.Yearly: return t("cycle.yearly", { defaultValue: "Yearly" });
    case BillingCycle.Trial: return t("cycle.trial", { defaultValue: "Trial" });
    case BillingCycle.Custom: return t("cycle.custom", { defaultValue: "Custom" });
    default: return "—";
  }
}

export function planTypeLabel(type: number, t: TFunction): string {
  switch (type) {
    case PlanType.Trial: return t("planType.trial", { defaultValue: "Trial" });
    case PlanType.Enterprise: return t("planType.enterprise", { defaultValue: "Enterprise" });
    default: return t("planType.standard", { defaultValue: "Standard" });
  }
}

export function rechargeStatusLabel(status: number, t: TFunction): string {
  switch (status) {
    case RechargeStatus.Pending: return t("recharge.status.pending", { defaultValue: "Awaiting confirmation" });
    case RechargeStatus.Active: return t("recharge.status.active", { defaultValue: "Active" });
    case RechargeStatus.Exhausted: return t("recharge.status.exhausted", { defaultValue: "Used up" });
    case RechargeStatus.Expired: return t("recharge.status.expired", { defaultValue: "Expired" });
    case RechargeStatus.Cancelled: return t("recharge.status.cancelled", { defaultValue: "Cancelled" });
    default: return String(status);
  }
}

export function rechargeStatusClass(status: number): string {
  switch (status) {
    case RechargeStatus.Active: return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 " + STATUS_CHIP;
    case RechargeStatus.Pending: return "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 " + STATUS_CHIP;
    default: return "bg-[rgb(var(--card-muted))] text-[rgb(var(--text-muted))] border-[rgb(var(--border))] " + STATUS_CHIP;
  }
}

export function meterKindLabel(kind: number, t: TFunction): string {
  return kind === MeterKind.Allowance
    ? t("meter.kind.allowance", { defaultValue: "Allowance (resets)" })
    : t("meter.kind.capacity", { defaultValue: "Capacity (current count)" });
}

export function limitTypeLabel(limit: number, t: TFunction): string {
  return limit === MeterLimitType.Soft
    ? t("meter.limit.soft", { defaultValue: "Soft limit" })
    : t("meter.limit.hard", { defaultValue: "Hard limit" });
}

/** Colour band for a usage bar: 0–64 green, 65–84 amber, 85+ red. Mirrors ProgressBar's own thresholds. */
export function usageTone(percent: number | null | undefined): "ok" | "warn" | "danger" {
  if (percent === null || percent === undefined) return "ok";
  if (percent >= 85) return "danger";
  if (percent >= 65) return "warn";
  return "ok";
}

/**
 * Whole calendar days from today until the given date, ignoring time of
 * day: a renewal on the 7th seen on the 8th of the previous year is 364
 * days away, not 365 because of a few leftover hours.
 */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const end = new Date(iso);
  if (isNaN(end.getTime())) return null;
  const today = new Date();
  const endDay = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((endDay - todayDay) / 86400000);
}

export function parseLevelOptions(csv: string | null | undefined): string[] {
  return (csv || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}


export function invoiceStatusLabel(status: number, t: TFunction): string {
  switch (status) {
    case InvoiceStatus.Draft: return t("billing.status.draft", { defaultValue: "Draft" });
    case InvoiceStatus.Issued: return t("billing.status.issued", { defaultValue: "Unpaid" });
    case InvoiceStatus.Paid: return t("billing.status.paid", { defaultValue: "Paid" });
    case InvoiceStatus.Overdue: return t("billing.status.overdue", { defaultValue: "Overdue" });
    case InvoiceStatus.Cancelled: return t("billing.status.cancelled", { defaultValue: "Cancelled" });
    default: return String(status);
  }
}

export function invoiceStatusClass(status: number): string {
  switch (status) {
    case InvoiceStatus.Paid: return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 " + STATUS_CHIP;
    case InvoiceStatus.Issued: return "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 " + STATUS_CHIP;
    case InvoiceStatus.Overdue: return "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30 " + STATUS_CHIP;
    default: return "bg-[rgb(var(--card-muted))] text-[rgb(var(--text-muted))] border-[rgb(var(--border))] " + STATUS_CHIP;
  }
}

export function invoiceTypeLabel(type: number, t: TFunction): string {
  switch (type) {
    case InvoiceType.Subscription: return t("billing.type.subscription", { defaultValue: "Subscription" });
    case InvoiceType.Recharge: return t("billing.type.recharge", { defaultValue: "Recharge" });
    default: return t("billing.type.adjustment", { defaultValue: "Adjustment" });
  }
}

export function paymentMethodLabel(method: number, t: TFunction): string {
  switch (method) {
    case PaymentMethod.BankTransfer: return t("billing.method.bankTransfer", { defaultValue: "Bank transfer" });
    case PaymentMethod.Upi: return t("billing.method.upi", { defaultValue: "UPI" });
    case PaymentMethod.Card: return t("billing.method.card", { defaultValue: "Card" });
    case PaymentMethod.Gateway: return t("billing.method.gateway", { defaultValue: "Payment gateway" });
    case PaymentMethod.Credit: return t("billing.method.credit", { defaultValue: "Credit / adjustment" });
    default: return t("billing.method.offline", { defaultValue: "Offline" });
  }
}

export function paymentStatusLabel(status: number, t: TFunction): string {
  switch (status) {
    case PaymentStatus.Pending: return t("billing.paymentStatus.pending", { defaultValue: "Pending" });
    case PaymentStatus.Failed: return t("billing.paymentStatus.failed", { defaultValue: "Failed" });
    case PaymentStatus.Refunded: return t("billing.paymentStatus.refunded", { defaultValue: "Refunded" });
    default: return t("billing.paymentStatus.succeeded", { defaultValue: "Received" });
  }
}


const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const below1000 = (n: number): string => {
  const parts: string[] = [];
  if (n >= 100) { parts.push(`${ONES[Math.floor(n / 100)]} Hundred`); n %= 100; }
  if (n >= 20) parts.push(`${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ""}`);
  else if (n > 0) parts.push(ONES[n]);
  return parts.join(" ");
};
/**
 * Spells an amount for the "Amount in words" line. Mirrors the server's
 * AmountInWords (Indian lakh/crore grouping for INR); used only where a
 * bill is composed on the client, such as the sample invoice preview.
 */
export function amountInWords(amount: number, currency = "INR"): string {
  const code = (currency || "INR").toUpperCase();
  const units: Record<string, [string, string]> = { INR: ["Rupees", "Paise"], USD: ["Dollars", "Cents"], EUR: ["Euros", "Cents"], GBP: ["Pounds", "Pence"], AED: ["Dirhams", "Fils"] };
  const [major, minor] = units[code] ?? [code, "Cents"];
  const abs = Math.round(Math.abs(amount) * 100) / 100;
  let whole = Math.floor(abs);
  const fraction = Math.round((abs - whole) * 100);
  const groups = code === "INR" ? [[10000000, "Crore"], [100000, "Lakh"], [1000, "Thousand"]] as const : [[1000000000, "Billion"], [1000000, "Million"], [1000, "Thousand"]] as const;
  const parts: string[] = [];
  for (const [div, label] of groups) { if (whole >= div) { parts.push(`${below1000(Math.floor(whole / div))} ${label}`); whole %= div; } }
  if (whole > 0) parts.push(below1000(whole));
  const words = parts.length ? parts.join(" ") : "Zero";
  return `${major} ${words}${fraction > 0 ? ` and ${below1000(fraction)} ${minor}` : ""} Only`;
}
